/**
 * Hook for searching entities with debouncing and request cancellation
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import type {
  EntitySearchResponse,
  EntityType,
  UseEntitySearchOptions,
  UseEntitySearchResult,
} from './types';

const DEBOUNCE_DELAY_MS = 250;
const MIN_QUERY_LENGTH = 2;
const API_TIMEOUT_MS = 5000;

/**
 * Custom hook for debouncing a value
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debouncedValue;
}

/**
 * Check if an error is retryable (transient) vs permanent
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof Error) {
    const { statusCode } = error as { statusCode?: number };
    // 4xx client errors are not retryable, except 429 (rate limiting) which should be retried
    if (
      statusCode &&
      statusCode >= 400 &&
      statusCode < 500 &&
      statusCode !== 429
    ) {
      return false;
    }
    // Network errors, timeouts, 5xx, and 429 are retryable
    return true;
  }
  return true;
}

/**
 * Hook for searching entities across the organization
 *
 * Features:
 * - 250ms debounce on query changes
 * - Cancels in-flight requests when query changes
 * - Skips API call for queries < 2 characters
 * - Supports filtering by entity types
 *
 * @param query - Search query string
 * @param options - Search options (types filter, enabled flag)
 * @returns Search results, loading state, error, and refetch function
 */
export function useEntitySearch(
  query: string,
  options: UseEntitySearchOptions = {}
): UseEntitySearchResult {
  const { types, enabled = true, includeHidden = false } = options;
  const { orgId } = useParams<{ orgId: string }>();

  const [results, setResults] = useState<EntitySearchResponse | null>(null);
  // Track the query and types that produced the current results (for analytics)
  const [resultQuery, setResultQuery] = useState<string | null>(null);
  const [resultTypes, setResultTypes] = useState<EntityType[] | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track whether error is retryable
  const isRetryable = useRef(true);

  // Track Retry-After header value (in seconds)
  const retryAfterRef = useRef<number | null>(null);

  // AbortController for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce the query
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_DELAY_MS);

  // Track fetch trigger for refetch
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Stabilize types array to prevent unnecessary re-fetches
  // Convert array to string for comparison (without sorting to preserve order)
  const typesKey = types?.join(',') ?? '';
  const stableTypes = useRef(types);
  if (typesKey !== (stableTypes.current?.join(',') ?? '')) {
    stableTypes.current = types;
  }

  const refetch = useCallback(() => {
    // Only allow refetch if error is retryable
    if (isRetryable.current) {
      setFetchTrigger(prev => prev + 1);
    }
  }, []);

  useEffect(() => {
    // Skip if disabled or no orgId
    if (!enabled || !orgId) {
      setResults(null);
      setResultQuery(null);
      setResultTypes(undefined);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Skip if query is too short
    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setResultQuery(null);
      setResultTypes(undefined);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Track whether abort was due to timeout (vs manual cancellation)
    let timedOut = false;

    const fetchResults = async () => {
      // Generate a unique requestId for this search
      const requestId = crypto.randomUUID();
      const startTime = performance.now();

      setIsLoading(true);
      setError(null);

      // Build URL with query params
      const params = new URLSearchParams();
      params.set('q', debouncedQuery);
      if (stableTypes.current && stableTypes.current.length > 0) {
        params.set('types', stableTypes.current.join(','));
      }
      if (includeHidden) {
        params.set('include_hidden', 'true');
      }

      const url = `/api/orgs/${orgId}/search/?${params.toString()}`;

      // Log query length only (not content) to prevent PII exposure
      logger.info('Entity search started', {
        requestId,
        query_length: debouncedQuery.length,
        types: stableTypes.current,
        orgId,
        component: 'useEntitySearch',
      });

      // Create a timeout that aborts the request after API_TIMEOUT_MS
      const timeoutId = setTimeout(() => {
        timedOut = true;
        abortController.abort();
      }, API_TIMEOUT_MS);

      try {
        const response = await fetchWithDefaults(
          url,
          {
            signal: abortController.signal,
          },
          API_TIMEOUT_MS
        );

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        if (!response.ok) {
          const statusCode = response.status;
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData.detail ||
            errorData.error ||
            `HTTP ${statusCode}: ${response.statusText}`;
          const apiError = new Error(errorMessage) as Error & {
            statusCode?: number;
            retryAfterHeader?: string | null;
          };
          apiError.statusCode = statusCode;
          // Attach Retry-After header if present
          apiError.retryAfterHeader = response.headers.get('Retry-After');

          // Log 429 rate limit with retry-after header
          if (statusCode === 429) {
            logger.warn('Entity search rate limited', {
              requestId,
              statusCode: 429,
              retryAfter: apiError.retryAfterHeader,
              component: 'useEntitySearch',
            });
          }

          throw apiError;
        }

        const data: EntitySearchResponse = await response.json();

        // Check again after parsing in case request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        // Calculate result count
        const resultCount =
          (data.users?.length ?? 0) +
          data.contacts.length +
          data.companies.length +
          data.deals.length;
        const duration_ms = Math.round(performance.now() - startTime);

        logger.info('Entity search completed', {
          requestId,
          query_length: debouncedQuery.length,
          resultCount,
          duration_ms,
          component: 'useEntitySearch',
        });

        setResults(data);
        setResultQuery(debouncedQuery);
        setResultTypes(stableTypes.current);
        setError(null);
        isRetryable.current = true;
        // Clear retryAfter on success to avoid stale values
        retryAfterRef.current = null;
      } catch (err) {
        // Handle abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          // If timeout caused the abort, surface error and clear loading
          if (timedOut) {
            logger.warn('Entity search timeout', {
              requestId,
              query_length: debouncedQuery.length,
              timeout: API_TIMEOUT_MS,
              component: 'useEntitySearch',
            });
            setError(new Error('Request timed out'));
            setResults(null);
            setResultQuery(null);
            setResultTypes(undefined);
            setIsLoading(false);
            isRetryable.current = true;
          } else {
            // Manual abort (query change/unmount) - log and ignore silently
            logger.debug('Entity search aborted', {
              requestId,
              reason: 'query_changed',
              component: 'useEntitySearch',
            });
          }
          return;
        }

        const errorInstance =
          err instanceof Error ? err : new Error(String(err));
        const { statusCode, retryAfterHeader } = errorInstance as {
          statusCode?: number;
          retryAfterHeader?: string | null;
        };

        logger.error('Entity search failed', errorInstance, {
          requestId,
          query_length: debouncedQuery.length,
          statusCode,
          component: 'useEntitySearch',
        });

        setError(errorInstance);
        setResults(null);
        setResultQuery(null);
        setResultTypes(undefined);
        isRetryable.current = isRetryableError(errorInstance);

        // Parse Retry-After header if error is 429
        if (statusCode === 429 && retryAfterHeader) {
          // Parse as decimal seconds (RFC 7231 delta-seconds format)
          const retryAfterSeconds = parseFloat(retryAfterHeader);
          if (!isNaN(retryAfterSeconds)) {
            // Clamp to max 60 seconds
            retryAfterRef.current = Math.min(retryAfterSeconds, 60);
          }
        } else {
          retryAfterRef.current = null;
        }
      } finally {
        clearTimeout(timeoutId);
        // Only update loading if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchResults();

    // Cleanup: abort request on unmount or when dependencies change
    return () => {
      abortController.abort();
    };
  }, [debouncedQuery, typesKey, enabled, orgId, fetchTrigger, includeHidden]);

  // Only expose retryAfter when error is actually a 429 to avoid stale values
  const statusCode = (error as { statusCode?: number } | null)?.statusCode;
  const retryAfter = statusCode === 429 ? retryAfterRef.current : null;

  return {
    results,
    resultQuery,
    resultTypes,
    isLoading,
    error,
    refetch,
    retryAfter,
  };
}
